import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useAdminAuth } from '../../contexts/AdminAuthContext';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Switch } from '../../components/ui/switch';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '../../components/ui/tabs';
import { 
  Plus, Pencil, Trash2, X, Save, Clock, 
  Calendar, Package, AlertCircle, ChevronDown, ChevronUp,
  Star, Copy, Eye, EyeOff, Check
} from 'lucide-react';
import { toast } from 'sonner';
import LanguageTabs, { useAvailableLanguages } from '../../components/admin/LanguageTabs';
import { useTranslations } from '../../context/TranslationsContext';

const API = process.env.REACT_APP_BACKEND_URL;

const DAY_GROUP_LABELS = {
  mon_thu: { icon: '1-4', color: 'text-blue-400' },
  fri: { icon: '5', color: 'text-purple-400' },
  sat_sun: { icon: '6-7', color: 'text-orange-400' },
  sat: { icon: '6', color: 'text-orange-400' },
  sun: { icon: '7', color: 'text-red-400' }
};

const AdminPricing = () => {
  const { getAuthHeader } = useAdminAuth();
  const { td: t } = useTranslations(['admin', 'common']);
  const { languages, loading: languagesLoading, defaultLanguage } = useAvailableLanguages();
  
  const [activeTab, setActiveTab] = useState('configs');
  const [configs, setConfigs] = useState([]);
  const [rentals, setRentals] = useState([]);
  const [loading, setLoading] = useState(true);
  
  // Expanded configs and day groups
  const [expandedConfigs, setExpandedConfigs] = useState({});
  const [expandedDayGroups, setExpandedDayGroups] = useState({});
  
  // Modals
  const [editingConfig, setEditingConfig] = useState(null);
  const [editingOption, setEditingOption] = useState(null);
  const [editingRental, setEditingRental] = useState(null);
  const [isCreating, setIsCreating] = useState(false);
  
  // Context for option editing
  const [optionContext, setOptionContext] = useState({ configId: null, dayGroupId: null });
  
  const [activeLangTab, setActiveLangTab] = useState('pl');

  useEffect(() => {
    fetchData();
  }, []);

  useEffect(() => {
    if (defaultLanguage) setActiveLangTab(defaultLanguage);
  }, [defaultLanguage]);

  const fetchData = async () => {
    try {
      const [configsRes, rentalsRes] = await Promise.all([
        axios.get(`${API}/api/pricing/configs`, { headers: getAuthHeader() }),
        axios.get(`${API}/api/pricing/rentals`, { headers: getAuthHeader() })
      ]);
      setConfigs(configsRes.data);
      setRentals(rentalsRes.data);
      
      // Auto-expand first config and all its day groups
      if (configsRes.data.length > 0) {
        const firstId = configsRes.data[0].config_id;
        setExpandedConfigs(prev => {
          const expanded = { ...prev };
          if (Object.keys(expanded).length === 0) {
            expanded[firstId] = true;
          }
          return expanded;
        });
        setExpandedDayGroups(prev => {
          const dgs = { ...prev };
          configsRes.data.forEach(c => {
            (c.dayGroups || []).forEach(dg => {
              const key = `${c.config_id}_${dg.dayGroup_id}`;
              if (dgs[key] === undefined) dgs[key] = true;
            });
          });
          return dgs;
        });
      }
    } catch (error) {
      console.error('Failed to fetch data:', error);
      toast.error('Nie udało się pobrać danych cennikowych');
    } finally {
      setLoading(false);
    }
  };

  // ==================== CONFIG HANDLERS ====================

  const handleCreateConfig = async (cloneFromId) => {
    try {
      const body = {
        name: { pl: 'Nowy harmonogram', en: 'New schedule' }
      };
      if (cloneFromId) body.cloneFrom = cloneFromId;
      
      const res = await axios.post(`${API}/api/pricing/configs`, body, { headers: getAuthHeader() });
      toast.success('Harmonogram utworzony');
      
      // Open edit modal for the new config
      if (res.data.config) {
        setEditingConfig(res.data.config);
        setIsCreating(true);
      }
      fetchData();
    } catch (error) {
      toast.error('Nie udało się utworzyć harmonogramu');
    }
  };

  const handleSaveConfig = async () => {
    try {
      await axios.put(
        `${API}/api/pricing/configs/${editingConfig.config_id}`,
        { 
          name: editingConfig.name, 
          isDefault: editingConfig.isDefault,
          startDate: editingConfig.startDate || null,
          endDate: editingConfig.endDate || null
        },
        { headers: getAuthHeader() }
      );
      toast.success('Harmonogram zapisany');
      setEditingConfig(null);
      setIsCreating(false);
      fetchData();
    } catch (error) {
      toast.error('Nie udało się zapisać');
    }
  };

  const handleActivateConfig = async (configId) => {
    try {
      await axios.put(`${API}/api/pricing/configs/${configId}/activate`, {}, { headers: getAuthHeader() });
      toast.success('Harmonogram aktywowany');
      fetchData();
    } catch (error) {
      toast.error('Nie udało się aktywować');
    }
  };

  const handleDeleteConfig = async (configId) => {
    if (!confirm('Czy na pewno chcesz usunąć ten harmonogram?')) return;
    try {
      await axios.delete(`${API}/api/pricing/configs/${configId}`, { headers: getAuthHeader() });
      toast.success('Harmonogram usunięty');
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.error || 'Nie udało się usunąć');
    }
  };

  // ==================== OPTION HANDLERS ====================

  const handleSaveOption = async () => {
    const { configId, dayGroupId } = optionContext;
    try {
      if (isCreating) {
        await axios.post(
          `${API}/api/pricing/configs/${configId}/daygroups/${dayGroupId}/options`,
          editingOption,
          { headers: getAuthHeader() }
        );
        toast.success('Opcja cenowa dodana');
      } else {
        await axios.put(
          `${API}/api/pricing/configs/${configId}/daygroups/${dayGroupId}/options/${editingOption.option_id}`,
          editingOption,
          { headers: getAuthHeader() }
        );
        toast.success('Opcja cenowa zaktualizowana');
      }
      setEditingOption(null);
      setOptionContext({ configId: null, dayGroupId: null });
      setIsCreating(false);
      fetchData();
    } catch (error) {
      toast.error('Nie udało się zapisać opcji');
    }
  };

  const handleDeleteOption = async (configId, dayGroupId, optionId) => {
    if (!confirm('Czy na pewno chcesz usunąć tę opcję cenową?')) return;
    try {
      await axios.delete(
        `${API}/api/pricing/configs/${configId}/daygroups/${dayGroupId}/options/${optionId}`,
        { headers: getAuthHeader() }
      );
      toast.success('Opcja usunięta');
      fetchData();
    } catch (error) {
      toast.error('Nie udało się usunąć');
    }
  };

  const handleToggleVisibility = async (configId, dayGroupId, optionId, currentVisible) => {
    try {
      await axios.put(
        `${API}/api/pricing/configs/${configId}/daygroups/${dayGroupId}/options/${optionId}/toggle`,
        { isVisible: !currentVisible },
        { headers: getAuthHeader() }
      );
      fetchData();
    } catch (error) {
      toast.error('Nie udało się zmienić widoczności');
    }
  };

  // ==================== RENTAL HANDLERS ====================
  
  const handleSaveRental = async () => {
    try {
      if (isCreating) {
        await axios.post(`${API}/api/pricing/rentals`, editingRental, { headers: getAuthHeader() });
        toast.success('Element wypożyczalni dodany');
      } else {
        await axios.put(
          `${API}/api/pricing/rentals/${editingRental.rental_id}`,
          editingRental,
          { headers: getAuthHeader() }
        );
        toast.success('Element wypożyczalni zaktualizowany');
      }
      setEditingRental(null);
      setIsCreating(false);
      fetchData();
    } catch (error) {
      toast.error('Nie udało się zapisać');
    }
  };

  const handleDeleteRental = async (rentalId) => {
    if (!confirm('Czy na pewno chcesz usunąć ten element?')) return;
    try {
      await axios.delete(`${API}/api/pricing/rentals/${rentalId}`, { headers: getAuthHeader() });
      toast.success('Element usunięty');
      fetchData();
    } catch (error) {
      toast.error('Nie udało się usunąć');
    }
  };

  const handleSplitDayGroup = async (configId, dayGroupId) => {
    if (dayGroupId !== 'sat_sun') return;
    if (!confirm('Rozdzielić "Sobota - Niedziela" na osobne grupy "Sobota" i "Niedziela"? Opcje cenowe zostaną skopiowane do obu grup.')) return;
    try {
      await axios.post(
        `${API}/api/pricing/configs/${configId}/daygroups/sat_sun/split`,
        {
          groups: [
            { dayGroup_id: 'sat', name: { pl: 'Sobota', en: 'Saturday' }, days: ['saturday'] },
            { dayGroup_id: 'sun', name: { pl: 'Niedziela', en: 'Sunday' }, days: ['sunday'] }
          ]
        },
        { headers: getAuthHeader() }
      );
      toast.success('Rozdzielono na Sobotę i Niedzielę');
      fetchData();
    } catch (error) {
      toast.error('Nie udało się rozdzielić');
    }
  };

  const handleMergeDayGroups = async (configId) => {
    if (!confirm('Połączyć "Sobota" i "Niedziela" z powrotem w "Sobota - Niedziela"? Sesje zostaną zresetowane.')) return;
    try {
      await axios.post(
        `${API}/api/pricing/configs/${configId}/merge-daygroups`,
        {
          sourceIds: ['sat', 'sun'],
          target: { dayGroup_id: 'sat_sun', name: { pl: 'Sobota - Niedziela', en: 'Saturday - Sunday' }, days: ['saturday', 'sunday'] }
        },
        { headers: getAuthHeader() }
      );
      toast.success('Połączono w Sobota - Niedziela');
      fetchData();
    } catch (error) {
      toast.error('Nie udało się połączyć');
    }
  };

  // ==================== MIGRATION ====================

  const handleMigrate = async () => {
    try {
      const res = await axios.post(`${API}/api/pricing/migrate`, {}, { headers: getAuthHeader() });
      toast.success(res.data.message || 'Migracja zakończona');
      fetchData();
    } catch (error) {
      toast.error('Migracja nie powiodła się');
    }
  };

  const toggleConfigExpand = (configId) => {
    setExpandedConfigs(prev => ({ ...prev, [configId]: !prev[configId] }));
  };

  const toggleDayGroupExpand = (configId, dgId) => {
    const key = `${configId}_${dgId}`;
    setExpandedDayGroups(prev => ({ ...prev, [key]: !prev[key] }));
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64" data-testid="pricing-loading">
        <div className="animate-spin rounded-full h-8 w-8 border-t-2 border-purple-500"></div>
      </div>
    );
  }

  return (
    <div className="space-y-8" data-testid="admin-pricing-page">
      <div>
        <h1 className="text-2xl font-bold text-white" data-testid="pricing-title">
          {t('admin.pricing.title', 'Zarządzanie Cennikiem')}
        </h1>
        <p className="text-gray-400">
          {t('admin.pricing.subtitle', 'Harmonogramy cenowe, wejścia i wypożyczalnia')}
        </p>
      </div>

      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList className="bg-white/5 border border-white/10">
          <TabsTrigger value="configs" className="data-[state=active]:bg-purple-600" data-testid="tab-configs">
            <Calendar className="w-4 h-4 mr-2" />
            {t('admin.pricing.schedules', 'Harmonogramy')}
          </TabsTrigger>
          <TabsTrigger value="rentals" className="data-[state=active]:bg-purple-600" data-testid="tab-rentals">
            <Package className="w-4 h-4 mr-2" />
            {t('admin.pricing.rentals', 'Wypożyczalnia')}
          </TabsTrigger>
        </TabsList>

        {/* ==================== CONFIGS TAB ==================== */}
        <TabsContent value="configs" className="space-y-6 mt-6">
          {/* Info & Actions */}
          <div className="flex items-center justify-between gap-4 flex-wrap">
            <div className="flex items-center gap-3 text-blue-400 bg-blue-500/10 p-3 rounded-lg flex-1 min-w-[300px]">
              <AlertCircle className="w-5 h-5 shrink-0" />
              <p className="text-sm">
                {t('admin.pricing.configInfo', 'Każdy harmonogram definiuje ceny dla grup dni (Pon-Czw, Pt, Sob-Ndz). Tylko jeden harmonogram może być aktywny jednocześnie.')}
              </p>
            </div>
            <div className="flex gap-2">
              {configs.length === 0 && (
                <Button
                  onClick={handleMigrate}
                  variant="outline"
                  className="border-yellow-500/30 text-yellow-400 hover:bg-yellow-500/10"
                  data-testid="btn-migrate"
                >
                  <AlertCircle className="w-4 h-4 mr-2" />
                  Migruj dane
                </Button>
              )}
              <Button
                onClick={() => handleCreateConfig(null)}
                className="bg-purple-600 hover:bg-purple-700"
                data-testid="btn-new-config"
              >
                <Plus className="w-4 h-4 mr-2" />
                {t('admin.pricing.newConfig', 'Nowy harmonogram')}
              </Button>
            </div>
          </div>

          {/* Config list */}
          {configs.map((config) => (
            <Card 
              key={config.config_id} 
              className={`border-white/10 transition-colors ${
                config.isActive 
                  ? 'bg-green-500/10 border-green-500/30' 
                  : 'bg-white/5'
              }`}
              data-testid={`config-${config.config_id}`}
            >
              <CardHeader 
                className="cursor-pointer hover:bg-white/5 transition-colors"
                onClick={() => toggleConfigExpand(config.config_id)}
              >
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <Calendar className={`w-5 h-5 ${config.isActive ? 'text-green-400' : 'text-gray-400'}`} />
                    <div>
                      <div className="flex items-center gap-2">
                        <CardTitle className="text-white">
                          {config.name?.[defaultLanguage] || config.name?.pl || 'Bez nazwy'}
                        </CardTitle>
                        {config.isActive && (
                          <span className="px-2 py-0.5 bg-green-500/20 text-green-400 text-xs rounded font-medium" data-testid="badge-active">
                            {t('admin.pricing.active', 'Aktywny')}
                          </span>
                        )}
                        {config.isDefault && (
                          <span className="px-2 py-0.5 bg-blue-500/20 text-blue-400 text-xs rounded font-medium" data-testid="badge-default">
                            {t('admin.pricing.default', 'Domyślny')}
                          </span>
                        )}
                      </div>
                      {/* Show secondary language */}
                      {config.name?.en && defaultLanguage !== 'en' && (
                        <p className="text-gray-400 text-sm">{config.name.en}</p>
                      )}
                      {/* Date range */}
                      {(config.startDate || config.endDate) && (
                        <p className="text-gray-500 text-xs mt-0.5">
                          {config.startDate ? new Date(config.startDate).toLocaleString('pl-PL', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' }) : '...'} 
                          {' — '}
                          {config.endDate ? new Date(config.endDate).toLocaleString('pl-PL', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' }) : '...'}
                        </p>
                      )}
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    {!config.isActive && (
                      <Button
                        size="sm"
                        onClick={(e) => { e.stopPropagation(); handleActivateConfig(config.config_id); }}
                        className="bg-green-600 hover:bg-green-700 text-white"
                        data-testid={`btn-activate-${config.config_id}`}
                      >
                        <Check className="w-3 h-3 mr-1" />
                        {t('admin.pricing.activate', 'Aktywuj')}
                      </Button>
                    )}
                    <Button
                      variant="ghost" size="icon"
                      onClick={(e) => { e.stopPropagation(); handleCreateConfig(config.config_id); }}
                      className="text-gray-400 hover:text-white" title="Klonuj harmonogram"
                      data-testid={`btn-clone-${config.config_id}`}
                    >
                      <Copy className="w-4 h-4" />
                    </Button>
                    <Button
                      variant="ghost" size="icon"
                      onClick={(e) => { e.stopPropagation(); setEditingConfig(config); setIsCreating(false); }}
                      className="text-gray-400 hover:text-white"
                      data-testid={`btn-edit-config-${config.config_id}`}
                    >
                      <Pencil className="w-4 h-4" />
                    </Button>
                    <Button
                      variant="ghost" size="icon"
                      onClick={(e) => { e.stopPropagation(); handleDeleteConfig(config.config_id); }}
                      className="text-gray-400 hover:text-red-400"
                      data-testid={`btn-delete-config-${config.config_id}`}
                    >
                      <Trash2 className="w-4 h-4" />
                    </Button>
                    {expandedConfigs[config.config_id] ? 
                      <ChevronUp className="w-5 h-5 text-gray-400" /> : 
                      <ChevronDown className="w-5 h-5 text-gray-400" />
                    }
                  </div>
                </div>
              </CardHeader>

              {expandedConfigs[config.config_id] && (
                <CardContent className="space-y-4 pt-0">
                  {/* Day Groups */}
                  {(config.dayGroups || [])
                    .sort((a, b) => (a.sortOrder || 0) - (b.sortOrder || 0))
                    .map((dg) => {
                      const dgKey = `${config.config_id}_${dg.dayGroup_id}`;
                      const dgMeta = DAY_GROUP_LABELS[dg.dayGroup_id] || { icon: '?', color: 'text-gray-400' };
                      const visibleCount = (dg.options || []).filter(o => o.isVisible !== false).length;
                      const totalCount = (dg.options || []).length;

                      return (
                        <div key={dg.dayGroup_id} className="border border-white/10 rounded-lg overflow-hidden" data-testid={`daygroup-${dg.dayGroup_id}`}>
                          {/* Day Group Header */}
                          <div 
                            className="flex items-center justify-between p-4 bg-white/5 cursor-pointer hover:bg-white/10 transition-colors"
                            onClick={() => toggleDayGroupExpand(config.config_id, dg.dayGroup_id)}
                          >
                            <div className="flex items-center gap-3">
                              <div className={`flex items-center justify-center w-8 h-8 rounded-md bg-white/10 font-mono text-sm font-bold ${dgMeta.color}`}>
                                {dgMeta.icon}
                              </div>
                              <div>
                                <h4 className="text-white font-medium">
                                  {dg.name?.[defaultLanguage] || dg.name?.pl}
                                </h4>
                                <p className="text-gray-500 text-xs">
                                  {visibleCount}/{totalCount} {t('admin.pricing.visibleOptions', 'widocznych opcji')}
                                </p>
                              </div>
                            </div>
                            <div className="flex items-center gap-2">
                              {/* Split/Merge buttons */}
                              {dg.dayGroup_id === 'sat_sun' && (
                                <Button
                                  variant="ghost" size="sm"
                                  onClick={(e) => { e.stopPropagation(); handleSplitDayGroup(config.config_id, dg.dayGroup_id); }}
                                  className="text-orange-400 hover:text-orange-300 text-xs h-7 px-2"
                                  title="Rozdziel na Sobotę i Niedzielę"
                                >
                                  Rozdziel Sob/Ndz
                                </Button>
                              )}
                              {(dg.dayGroup_id === 'sat' || dg.dayGroup_id === 'sun') && (
                                <Button
                                  variant="ghost" size="sm"
                                  onClick={(e) => { e.stopPropagation(); handleMergeDayGroups(config.config_id); }}
                                  className="text-orange-400 hover:text-orange-300 text-xs h-7 px-2"
                                  title="Połącz z powrotem w Sob-Ndz"
                                >
                                  Połącz Sob+Ndz
                                </Button>
                              )}
                              {expandedDayGroups[dgKey] ? 
                                <ChevronUp className="w-4 h-4 text-gray-400" /> : 
                                <ChevronDown className="w-4 h-4 text-gray-400" />
                              }
                            </div>
                          </div>

                          {/* Day Group Options */}
                          {expandedDayGroups[dgKey] && (
                            <div className="p-4 space-y-3">
                              {(dg.options || [])
                                .sort((a, b) => (a.sortOrder || 0) - (b.sortOrder || 0))
                                .map((option) => (
                                  <div 
                                    key={option.option_id}
                                    className={`mb-2 p-3 rounded-lg transition-colors ${
                                      option.isVisible !== false 
                                        ? 'bg-white/5' 
                                        : 'bg-white/[0.02] opacity-50'
                                    }`}
                                    data-testid={`option-${option.option_id}`}
                                  >
                                    <div className="flex items-center justify-between">
                                      <div className="flex items-center gap-3 flex-1 min-w-0">
                                        {/* Visibility toggle */}
                                        <button
                                          onClick={() => handleToggleVisibility(config.config_id, dg.dayGroup_id, option.option_id, option.isVisible !== false)}
                                          className={`p-1.5 rounded-md transition-colors ${
                                            option.isVisible !== false 
                                              ? 'bg-green-500/20 text-green-400 hover:bg-green-500/30' 
                                              : 'bg-gray-700/50 text-gray-500 hover:bg-gray-700'
                                          }`}
                                          title={option.isVisible !== false ? 'Widoczny na stronie' : 'Ukryty na stronie'}
                                          data-testid={`toggle-vis-${option.option_id}`}
                                        >
                                          {option.isVisible !== false ? <Eye className="w-4 h-4" /> : <EyeOff className="w-4 h-4" />}
                                        </button>
                                        
                                        <div className="min-w-0">
                                          <h5 className="text-white font-medium text-sm truncate">
                                            {option.name?.[defaultLanguage] || option.name?.pl}
                                          </h5>
                                          <p className="text-gray-500 text-xs truncate">
                                            {option.type}
                                          </p>
                                        </div>
                                      </div>
                                      
                                      <div className="flex items-center gap-4 shrink-0">
                                        <div className="text-right">
                                          <p className="text-white font-bold text-sm">{option.price} zł</p>
                                          <p className="text-gray-500 text-[10px]">
                                            {t('admin.pricing.walkIn', 'bez rez.')}
                                          </p>
                                        </div>
                                        {option.priceWithReservation != null && (
                                          <div className="text-right">
                                            <p className="text-green-400 font-bold text-sm">{option.priceWithReservation} zł</p>
                                            <p className="text-gray-500 text-[10px]">
                                              {t('admin.pricing.withRes', 'z rez.')}
                                            </p>
                                          </div>
                                        )}
                                        <div className="flex items-center gap-1">
                                          <Button
                                            variant="ghost" size="icon"
                                            onClick={() => {
                                              setEditingOption({ ...option });
                                              setOptionContext({ configId: config.config_id, dayGroupId: dg.dayGroup_id });
                                              setIsCreating(false);
                                            }}
                                            className="text-gray-400 hover:text-white h-8 w-8"
                                            data-testid={`btn-edit-option-${option.option_id}`}
                                          >
                                            <Pencil className="w-3.5 h-3.5" />
                                          </Button>
                                          <Button
                                            variant="ghost" size="icon"
                                            onClick={() => handleDeleteOption(config.config_id, dg.dayGroup_id, option.option_id)}
                                            className="text-gray-400 hover:text-red-400 h-8 w-8"
                                            data-testid={`btn-delete-option-${option.option_id}`}
                                          >
                                            <Trash2 className="w-3.5 h-3.5" />
                                          </Button>
                                        </div>
                                      </div>
                                    </div>
                                    {/* Session slots badges */}
                                    {option.sessionSlots && option.sessionSlots.length > 0 && (
                                      <div className="mt-2 ml-10 flex flex-wrap gap-1.5">
                                        {option.sessionSlots.map((slot, si) => (
                                          <span 
                                            key={si}
                                            className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-mono ${
                                              slot.isVisible !== false
                                                ? 'bg-purple-500/20 text-purple-300 border border-purple-500/30'
                                                : 'bg-gray-700/30 text-gray-500 border border-gray-600/30 line-through'
                                            }`}
                                          >
                                            <Clock className="w-3 h-3" />
                                            {slot.time}
                                          </span>
                                        ))}
                                      </div>
                                    )}
                                  </div>
                                ))}

                              {/* Add option button */}
                              <Button
                                variant="outline"
                                size="sm"
                                onClick={() => {
                                  const sortOrder = (dg.options?.length || 0) + 1;
                                  setEditingOption({
                                    name: {},
                                    type: 'fullday',
                                    price: 0,
                                    priceWithReservation: null,
                                    isVisible: true,
                                    sortOrder
                                  });
                                  setOptionContext({ configId: config.config_id, dayGroupId: dg.dayGroup_id });
                                  setIsCreating(true);
                                }}
                                className="w-full border-dashed border-white/20 text-gray-400 hover:text-white"
                                data-testid={`btn-add-option-${config.config_id}-${dg.dayGroup_id}`}
                              >
                                <Plus className="w-4 h-4 mr-2" />
                                {t('admin.pricing.addOption', 'Dodaj opcję cenową')}
                              </Button>
                            </div>
                          )}
                        </div>
                      );
                    })}
                </CardContent>
              )}
            </Card>
          ))}

          {configs.length === 0 && (
            <Card className="bg-white/5 border-white/10">
              <CardContent className="p-8 text-center">
                <Calendar className="w-12 h-12 mx-auto text-gray-500 mb-4" />
                <p className="text-gray-400 mb-4">{t('admin.pricing.noConfigs', 'Brak harmonogramów cenowych')}</p>
                <div className="flex justify-center gap-3">
                  <Button onClick={handleMigrate} variant="outline" className="border-yellow-500/30 text-yellow-400">
                    <AlertCircle className="w-4 h-4 mr-2" />
                    Migruj istniejące dane
                  </Button>
                  <Button onClick={() => handleCreateConfig(null)} className="bg-purple-600 hover:bg-purple-700">
                    <Plus className="w-4 h-4 mr-2" />
                    Utwórz nowy harmonogram
                  </Button>
                </div>
              </CardContent>
            </Card>
          )}
        </TabsContent>

        {/* ==================== RENTALS TAB ==================== */}
        <TabsContent value="rentals" className="space-y-6 mt-6">
          <div className="flex items-center justify-between">
            <p className="text-gray-400 text-sm">
              {t('admin.pricing.rentalNote', 'Cena uwzględnia wypożyczenie sprzętu na czas pobytu.')}
            </p>
            <Button
              onClick={() => {
                setEditingRental({
                  name: {},
                  price: 0,
                  sortOrder: rentals.length + 1,
                  isActive: true
                });
                setIsCreating(true);
              }}
              className="bg-purple-600 hover:bg-purple-700"
              data-testid="btn-add-rental"
            >
              <Plus className="w-4 h-4 mr-2" />
              {t('admin.pricing.addEquipment', 'Dodaj sprzęt')}
            </Button>
          </div>

          <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-4">
            {rentals.map((rental) => (
              <Card key={rental.rental_id} className="bg-white/5 border-white/10" data-testid={`rental-${rental.rental_id}`}>
                <CardContent className="p-4">
                  <div className="flex items-start justify-between">
                    <div className="flex-1">
                      <div className="flex items-center gap-2">
                        <h3 className="text-white font-medium">{rental.name?.[defaultLanguage] || rental.name?.pl}</h3>
                        {!rental.isActive && (
                          <span className="px-2 py-0.5 bg-gray-700 text-gray-400 text-xs rounded">
                            {t('admin.pricing.inactive', 'Nieaktywny')}
                          </span>
                        )}
                      </div>
                      {rental.name?.en && defaultLanguage !== 'en' && (
                        <p className="text-gray-400 text-sm">{rental.name.en}</p>
                      )}
                    </div>
                    <div className="flex items-center gap-1">
                      <Button
                        variant="ghost" size="icon"
                        onClick={() => { setEditingRental(rental); setIsCreating(false); }}
                        className="text-gray-400 hover:text-white"
                        data-testid={`btn-edit-rental-${rental.rental_id}`}
                      >
                        <Pencil className="w-4 h-4" />
                      </Button>
                      <Button
                        variant="ghost" size="icon"
                        onClick={() => handleDeleteRental(rental.rental_id)}
                        className="text-gray-400 hover:text-red-400"
                        data-testid={`btn-delete-rental-${rental.rental_id}`}
                      >
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </div>
                  </div>
                  <div className="mt-4 text-center p-3 bg-white/5 rounded">
                    <p className="text-purple-400 font-bold text-2xl">{rental.price} zł</p>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>

          {rentals.length === 0 && (
            <Card className="bg-white/5 border-white/10">
              <CardContent className="p-8 text-center">
                <Package className="w-12 h-12 mx-auto text-gray-500 mb-4" />
                <p className="text-gray-400">{t('admin.pricing.noRentals', 'Brak elementów wypożyczalni')}</p>
              </CardContent>
            </Card>
          )}
        </TabsContent>
      </Tabs>

      {/* ==================== EDIT CONFIG MODAL ==================== */}
      {editingConfig && (
        <div className="fixed inset-0 bg-black/80 z-50 flex items-center justify-center p-4" data-testid="config-modal">
          <Card className="bg-gray-900 border-white/10 w-full max-w-lg">
            <CardHeader className="flex flex-row items-center justify-between">
              <CardTitle className="text-white">
                {isCreating ? t('admin.pricing.newConfigTitle', 'Nowy harmonogram cenowy') : t('admin.pricing.editConfigTitle', 'Edytuj harmonogram')}
              </CardTitle>
              <Button variant="ghost" size="icon" onClick={() => { setEditingConfig(null); setIsCreating(false); }}>
                <X className="w-5 h-5" />
              </Button>
            </CardHeader>
            <CardContent className="space-y-6">
              <div className="space-y-3">
                <Label className="text-gray-300">{t('admin.common.name', 'Nazwa')}</Label>
                <LanguageTabs
                  activeTab={activeLangTab}
                  onTabChange={setActiveLangTab}
                  showIndicator={true}
                  hasContent={Object.fromEntries(languages.map(l => [l.code, !!editingConfig.name?.[l.code]]))}
                />
                <Input
                  value={editingConfig.name?.[activeLangTab] || ''}
                  onChange={(e) => setEditingConfig({ 
                    ...editingConfig, 
                    name: { ...editingConfig.name, [activeLangTab]: e.target.value } 
                  })}
                  placeholder={activeLangTab === 'pl' ? 'np. Rok szkolny 2026/2027' : 'e.g. School Year 2026/2027'}
                  className="bg-white/5 border-white/10 text-white"
                  data-testid="config-name-input"
                />
              </div>

              <div className="flex items-center gap-3">
                <Switch
                  checked={editingConfig.isDefault || false}
                  onCheckedChange={(checked) => setEditingConfig({ ...editingConfig, isDefault: checked })}
                  data-testid="config-default-switch"
                />
                <div>
                  <Label className="text-gray-300">{t('admin.pricing.setAsDefault', 'Ustaw jako domyślny')}</Label>
                  <p className="text-gray-500 text-xs">
                    {t('admin.pricing.defaultInfo', 'Harmonogram domyślny jest używany gdy żaden inny nie jest aktywny')}
                  </p>
                </div>
              </div>

              {/* Date range */}
              <div className="space-y-3">
                <Label className="text-gray-300">{t('admin.pricing.dateRange', 'Okres obowiązywania')}</Label>
                <p className="text-gray-500 text-xs">
                  {t('admin.pricing.dateRangeInfo', 'Zostaw puste dla harmonogramu bez ograniczeń czasowych (np. domyślny). System automatycznie wybierze harmonogram według dat.')}
                </p>
                <div className="grid grid-cols-2 gap-3">
                  <div className="space-y-1">
                    <Label className="text-gray-400 text-xs">{t('admin.pricing.from', 'Od')}</Label>
                    <Input
                      type="datetime-local"
                      value={editingConfig.startDate ? new Date(editingConfig.startDate).toISOString().slice(0, 16) : ''}
                      onChange={(e) => setEditingConfig({ ...editingConfig, startDate: e.target.value || null })}
                      className="bg-white/5 border-white/10 text-white"
                      data-testid="config-start-date"
                    />
                  </div>
                  <div className="space-y-1">
                    <Label className="text-gray-400 text-xs">{t('admin.pricing.to', 'Do')}</Label>
                    <Input
                      type="datetime-local"
                      value={editingConfig.endDate ? new Date(editingConfig.endDate).toISOString().slice(0, 16) : ''}
                      onChange={(e) => setEditingConfig({ ...editingConfig, endDate: e.target.value || null })}
                      className="bg-white/5 border-white/10 text-white"
                      data-testid="config-end-date"
                    />
                  </div>
                </div>
              </div>

              <div className="flex justify-end gap-3 pt-4 border-t border-white/10">
                <Button variant="outline" onClick={() => { setEditingConfig(null); setIsCreating(false); }}>
                  {t('admin.common.cancel', 'Anuluj')}
                </Button>
                <Button onClick={handleSaveConfig} className="bg-purple-600 hover:bg-purple-700" data-testid="btn-save-config">
                  <Save className="w-4 h-4 mr-2" />
                  {t('admin.common.save', 'Zapisz')}
                </Button>
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      {/* ==================== EDIT OPTION MODAL ==================== */}
      {editingOption && (
        <div className="fixed inset-0 bg-black/80 z-50 flex items-center justify-center p-4" data-testid="option-modal">
          <Card className="bg-gray-900 border-white/10 w-full max-w-lg">
            <CardHeader className="flex flex-row items-center justify-between">
              <CardTitle className="text-white">
                {isCreating ? t('admin.pricing.newOption', 'Nowa opcja cenowa') : t('admin.pricing.editOption', 'Edytuj opcję cenową')}
              </CardTitle>
              <Button variant="ghost" size="icon" onClick={() => { setEditingOption(null); setIsCreating(false); }}>
                <X className="w-5 h-5" />
              </Button>
            </CardHeader>
            <CardContent className="space-y-6">
              {/* Name */}
              <div className="space-y-3">
                <Label className="text-gray-300">{t('admin.common.name', 'Nazwa')}</Label>
                <LanguageTabs
                  activeTab={activeLangTab}
                  onTabChange={setActiveLangTab}
                  showIndicator={true}
                  hasContent={Object.fromEntries(languages.map(l => [l.code, !!editingOption.name?.[l.code]]))}
                />
                <Input
                  value={editingOption.name?.[activeLangTab] || ''}
                  onChange={(e) => setEditingOption({ 
                    ...editingOption, 
                    name: { ...editingOption.name, [activeLangTab]: e.target.value } 
                  })}
                  placeholder={activeLangTab === 'pl' ? 'np. Wejście całodniowe' : 'e.g. Full day entry'}
                  className="bg-white/5 border-white/10 text-white"
                  data-testid="option-name-input"
                />
              </div>

              {/* Type */}
              <div className="space-y-2">
                <Label className="text-gray-300">{t('admin.pricing.optionType', 'Typ')}</Label>
                <select
                  value={editingOption.type || 'fullday'}
                  onChange={(e) => setEditingOption({ ...editingOption, type: e.target.value })}
                  className="w-full px-3 py-2 bg-white/5 border border-white/10 text-white rounded-md"
                  data-testid="option-type-select"
                >
                  <option value="fullday">Całodniowe</option>
                  <option value="2h">Sesja 2h</option>
                  <option value="1h">Sesja 1h</option>
                  <option value="lasthour">Ostatnia godzina</option>
                  <option value="other">Inne</option>
                </select>
              </div>

              {/* Prices */}
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label className="text-gray-300">{t('admin.pricing.priceWithoutRes', 'Cena bez rezerwacji (PLN)')}</Label>
                  <Input
                    type="number"
                    value={editingOption.price || 0}
                    onChange={(e) => setEditingOption({ ...editingOption, price: parseFloat(e.target.value) || 0 })}
                    className="bg-white/5 border-white/10 text-white"
                    data-testid="option-price-input"
                  />
                </div>
                <div className="space-y-2">
                  <Label className="text-gray-300">{t('admin.pricing.priceWithRes', 'Cena z rezerwacją (PLN)')}</Label>
                  <Input
                    type="number"
                    value={editingOption.priceWithReservation ?? ''}
                    onChange={(e) => setEditingOption({ 
                      ...editingOption, 
                      priceWithReservation: e.target.value === '' ? null : parseFloat(e.target.value) 
                    })}
                    placeholder="—"
                    className="bg-white/5 border-white/10 text-white"
                    data-testid="option-price-res-input"
                  />
                  <p className="text-gray-500 text-xs">{t('admin.pricing.leaveEmpty', 'Zostaw puste jeśli brak rozróżnienia')}</p>
                </div>
              </div>

              {/* Sort & Visibility */}
              <div className="flex items-center justify-between">
                <div className="space-y-2">
                  <Label className="text-gray-300">{t('admin.common.sortOrder', 'Kolejność')}</Label>
                  <Input
                    type="number"
                    value={editingOption.sortOrder || 0}
                    onChange={(e) => setEditingOption({ ...editingOption, sortOrder: parseInt(e.target.value) || 0 })}
                    className="bg-white/5 border-white/10 text-white w-24"
                    data-testid="option-sort-input"
                  />
                </div>
                <div className="flex items-center gap-3">
                  <Switch
                    checked={editingOption.isVisible !== false}
                    onCheckedChange={(checked) => setEditingOption({ ...editingOption, isVisible: checked })}
                    data-testid="option-visible-switch"
                  />
                  <div>
                    <Label className="text-gray-300">{t('admin.pricing.visible', 'Widoczny')}</Label>
                    <p className="text-gray-500 text-xs">{t('admin.pricing.visibleInfo', 'Pokaż na stronie cennika')}</p>
                  </div>
                </div>
              </div>

              {/* Session Slots */}
              <div className="space-y-3">
                <Label className="text-gray-300">{t('admin.pricing.sessionSlots', 'Przedziały czasowe sesji')}</Label>
                <p className="text-gray-500 text-xs">
                  {t('admin.pricing.sessionSlotsInfo', 'Dodaj dostępne godziny sesji (np. 10:00-12:00). Wyświetlą się jako badge pod opcją cenową.')}
                </p>
                <div className="space-y-2">
                  {(editingOption.sessionSlots || []).map((slot, si) => (
                    <div key={si} className="flex items-center gap-2">
                      <Input
                        value={slot.time || ''}
                        onChange={(e) => {
                          const slots = [...(editingOption.sessionSlots || [])];
                          slots[si] = { ...slots[si], time: e.target.value };
                          setEditingOption({ ...editingOption, sessionSlots: slots });
                        }}
                        placeholder="np. 10:00-12:00"
                        className="bg-white/5 border-white/10 text-white flex-1 font-mono text-sm"
                        data-testid={`slot-time-${si}`}
                      />
                      <button
                        type="button"
                        onClick={() => {
                          const slots = [...(editingOption.sessionSlots || [])];
                          slots[si] = { ...slots[si], isVisible: !slots[si].isVisible };
                          setEditingOption({ ...editingOption, sessionSlots: slots });
                        }}
                        className={`p-2 rounded-md transition-colors ${
                          slot.isVisible !== false 
                            ? 'bg-green-500/20 text-green-400' 
                            : 'bg-gray-700/50 text-gray-500'
                        }`}
                        title={slot.isVisible !== false ? 'Widoczny' : 'Ukryty'}
                        data-testid={`slot-toggle-${si}`}
                      >
                        {slot.isVisible !== false ? <Eye className="w-4 h-4" /> : <EyeOff className="w-4 h-4" />}
                      </button>
                      <Button
                        variant="ghost" size="icon"
                        onClick={() => {
                          const slots = (editingOption.sessionSlots || []).filter((_, i) => i !== si);
                          setEditingOption({ ...editingOption, sessionSlots: slots });
                        }}
                        className="text-gray-400 hover:text-red-400 h-8 w-8"
                        data-testid={`slot-delete-${si}`}
                      >
                        <Trash2 className="w-3.5 h-3.5" />
                      </Button>
                    </div>
                  ))}
                  <Button
                    variant="outline"
                    size="sm"
                    type="button"
                    onClick={() => {
                      const slots = [...(editingOption.sessionSlots || []), { time: '', isVisible: true }];
                      setEditingOption({ ...editingOption, sessionSlots: slots });
                    }}
                    className="w-full border-dashed border-white/20 text-gray-400 hover:text-white"
                    data-testid="btn-add-slot"
                  >
                    <Plus className="w-4 h-4 mr-2" />
                    {t('admin.pricing.addSlot', 'Dodaj przedział czasowy')}
                  </Button>
                </div>
              </div>

              {/* Actions */}
              <div className="flex justify-end gap-3 pt-4 border-t border-white/10">
                <Button variant="outline" onClick={() => { setEditingOption(null); setIsCreating(false); }}>
                  {t('admin.common.cancel', 'Anuluj')}
                </Button>
                <Button onClick={handleSaveOption} className="bg-purple-600 hover:bg-purple-700" data-testid="btn-save-option">
                  <Save className="w-4 h-4 mr-2" />
                  {t('admin.common.save', 'Zapisz')}
                </Button>
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      {/* ==================== EDIT RENTAL MODAL ==================== */}
      {editingRental && (
        <div className="fixed inset-0 bg-black/80 z-50 flex items-center justify-center p-4" data-testid="rental-modal">
          <Card className="bg-gray-900 border-white/10 w-full max-w-lg">
            <CardHeader className="flex flex-row items-center justify-between">
              <CardTitle className="text-white">
                {isCreating ? t('admin.pricing.newEquipment', 'Nowy sprzęt') : t('admin.pricing.editEquipment', 'Edytuj sprzęt')}
              </CardTitle>
              <Button variant="ghost" size="icon" onClick={() => { setEditingRental(null); setIsCreating(false); }}>
                <X className="w-5 h-5" />
              </Button>
            </CardHeader>
            <CardContent className="space-y-6">
              <div className="space-y-3">
                <Label className="text-gray-300">{t('admin.common.name', 'Nazwa')}</Label>
                <LanguageTabs
                  activeTab={activeLangTab}
                  onTabChange={setActiveLangTab}
                  showIndicator={true}
                  hasContent={Object.fromEntries(languages.map(l => [l.code, !!editingRental.name?.[l.code]]))}
                />
                <Input
                  value={editingRental.name?.[activeLangTab] || ''}
                  onChange={(e) => setEditingRental({ 
                    ...editingRental, 
                    name: { ...editingRental.name, [activeLangTab]: e.target.value } 
                  })}
                  placeholder={activeLangTab === 'pl' ? 'np. Hulajnoga + Kask' : 'e.g. Scooter + Helmet'}
                  className="bg-white/5 border-white/10 text-white"
                  data-testid="rental-name-input"
                />
              </div>

              <div className="space-y-2">
                <Label className="text-gray-300">{t('admin.pricing.price', 'Cena (PLN)')}</Label>
                <Input
                  type="number"
                  value={editingRental.price || 0}
                  onChange={(e) => setEditingRental({ ...editingRental, price: parseFloat(e.target.value) || 0 })}
                  className="bg-white/5 border-white/10 text-white"
                  data-testid="rental-price-input"
                />
              </div>

              <div className="flex items-center justify-between">
                <div className="space-y-2">
                  <Label className="text-gray-300">{t('admin.common.sortOrder', 'Kolejność')}</Label>
                  <Input
                    type="number"
                    value={editingRental.sortOrder || 0}
                    onChange={(e) => setEditingRental({ ...editingRental, sortOrder: parseInt(e.target.value) || 0 })}
                    className="bg-white/5 border-white/10 text-white w-24"
                    data-testid="rental-sort-input"
                  />
                </div>
                <div className="flex items-center gap-3">
                  <Switch
                    checked={editingRental.isActive}
                    onCheckedChange={(checked) => setEditingRental({ ...editingRental, isActive: checked })}
                    data-testid="rental-active-switch"
                  />
                  <Label className="text-gray-300">{t('admin.common.isActive', 'Aktywny')}</Label>
                </div>
              </div>

              <div className="flex justify-end gap-3 pt-4 border-t border-white/10">
                <Button variant="outline" onClick={() => { setEditingRental(null); setIsCreating(false); }}>
                  {t('admin.common.cancel', 'Anuluj')}
                </Button>
                <Button onClick={handleSaveRental} className="bg-purple-600 hover:bg-purple-700" data-testid="btn-save-rental">
                  <Save className="w-4 h-4 mr-2" />
                  {t('admin.common.save', 'Zapisz')}
                </Button>
              </div>
            </CardContent>
          </Card>
        </div>
      )}
    </div>
  );
};

export default AdminPricing;
